import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Check, Loader2, X, ArrowUpRight } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [status, setStatus] = useState("waiting"); // waiting | paid | expired | error
  const [via, setVia] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // Single webhook-driven long-poll (server waits up to 25s for the
        // Stripe webhook to update the DB, then falls back to one Stripe
        // API check if needed).
        const r = await api.get(`/payments/await/${sessionId}`, { timeout: 40000 });
        if (cancelled) return;
        setVia(r.data?.via || null);
        if (r.data?.payment_status === "paid") {
          setStatus("paid");
          await refresh();
        } else if (r.data?.status === "expired") {
          setStatus("expired");
        } else {
          setStatus("error");
        }
      } catch (e) {
        if (!cancelled) setStatus("error");
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <main className="crown-container py-24 text-center" data-testid="payment-success-page">
      {status === "waiting" && (
        <div data-testid="payment-waiting">
          <Loader2 className="animate-spin text-gold mx-auto mb-6" strokeWidth={1.5} size={28} />
          <p className="font-overline mb-3">Awaiting confirmation</p>
          <h1 className="font-serif text-4xl">Listening for Stripe…</h1>
          <p className="text-ash mt-3 text-sm">We&rsquo;ll know the moment the webhook arrives.</p>
        </div>
      )}
      {status === "paid" && (
        <div data-testid="payment-paid">
          <div className="w-16 h-16 bg-gold mx-auto mb-6 flex items-center justify-center"><Check className="text-espresso" strokeWidth={1.5} /></div>
          <p className="font-overline text-gold mb-3">Welcome to the atelier</p>
          <h1 className="font-serif text-5xl mb-4">You are <span className="italic">Premium.</span></h1>
          <p className="text-ash mb-2">Unlimited analyses · virtual try-on · stylist guides.</p>
          {via && <p className="font-overline text-ash mb-8" data-testid="payment-via">Confirmed via {via}</p>}
          <Link to="/dashboard" className="crown-btn-primary" data-testid="payment-dashboard-cta">
            Go to dashboard <ArrowUpRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
      )}
      {status === "expired" && (
        <div data-testid="payment-expired">
          <X className="text-bone mx-auto mb-6" strokeWidth={1.5} />
          <h1 className="font-serif text-4xl">Session expired</h1>
          <p className="text-ash mb-6">Please try again.</p>
          <Link to="/pricing" className="crown-btn-primary">Back to pricing</Link>
        </div>
      )}
      {status === "error" && (
        <div data-testid="payment-error">
          <X className="text-bone mx-auto mb-6" strokeWidth={1.5} />
          <h1 className="font-serif text-4xl">Still waiting</h1>
          <p className="text-ash mb-6">If you completed payment, refresh in a moment — Stripe webhooks can take a few seconds.</p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => window.location.reload()} className="crown-btn-ghost" data-testid="payment-retry-btn">Check again</button>
            <Link to="/dashboard" className="crown-btn-primary">Back to dashboard</Link>
          </div>
        </div>
      )}
    </main>
  );
}
